'use client';

import { useMemo, useState, useEffect, type FormEvent } from 'react';
import Link from 'next/link';
import { Star, ThumbsUp, ThumbsDown, Reply as ReplyIcon, Send } from 'lucide-react';
import { createClient } from '@/lib/supabase-browser';

type ReplyItem = {
  id: string;
  review_id: string;
  user_id: string;
  reply_text: string;
  created_at: string;
};

type ReviewItem = {
  id: string;
  user_id: string;
  rating: number;
  review_text: string;
  created_at: string;
  likes_count: number;
  dislikes_count: number;
  replies?: ReplyItem[];
};

type ToolCommunityReviewsProps = {
  toolId: string;
  toolName: string;
  isLoggedIn?: boolean;
  favoritesCount: number;
  monthlyViews: number;
  initialReviews: ReviewItem[];
};

function StarsInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Star rating">
      {[1, 2, 3, 4, 5].map((starValue) => (
        <button
          key={starValue}
          type="button"
          role="radio"
          aria-checked={value === starValue}
          onClick={() => onChange(starValue)}
          className="rounded-md p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70"
          aria-label={`${starValue} star`}
        >
          <Star
            className={`h-5 w-5 ${starValue <= value ? 'fill-amber-300 text-amber-300' : 'text-muted-foreground'}`}
          />
        </button>
      ))}
    </div>
  );
}

export default function ToolCommunityReviews({
  toolId,
  toolName,
  isLoggedIn = false,
  favoritesCount,
  monthlyViews,
  initialReviews,
}: ToolCommunityReviewsProps) {
  const supabase = useMemo(() => createClient(), []);
  const [userLoggedIn, setUserLoggedIn] = useState(isLoggedIn);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [reviews, setReviews] = useState(initialReviews);
  const [page, setPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [activeReplyId, setActiveReplyId] = useState<string | null>(null);
  const [singleReplyText, setSingleReplyText] = useState('');

  useEffect(() => {
    let active = true;
    supabase.auth.getUser().then(({ data }: any) => {
      if (active && data?.user) {
        setUserLoggedIn(true);
      }
    });
    return () => { active = false; };
  }, [supabase]);

  const totalPages = Math.max(1, Math.ceil(reviews.length / 10));
  const paginatedReviews = reviews.slice((page - 1) * 10, page * 10);

  const handleSubmitReview = async (event: FormEvent) => {
    event.preventDefault();
    if (!userLoggedIn || !reviewText.trim() || submitting) return;

    setSubmitting(true);
    setStatusMessage('');
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setStatusMessage('Please login to submit a review.');
        return;
      }

      const payload = {
        tool_id: toolId,
        user_id: user.id,
        rating,
        review_text: reviewText.trim(),
      };

      const { data, error } = await (supabase as any)
        .from('reviews')
        .insert(payload)
        .select('id, user_id, rating, review_text, created_at, likes_count, dislikes_count')
        .single();

      if (error) {
        setStatusMessage('Review table is not ready yet. Please apply DB migration first.');
        return;
      }

      setReviews((prev) => [{ ...data, replies: [] } as ReviewItem, ...prev]);
      setReviewText('');
      setRating(5);
      setPage(1);
      setStatusMessage('Review submitted successfully.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (reviewId: string, type: 'like' | 'dislike') => {
    if (!userLoggedIn) return;
    const targetColumn = type === 'like' ? 'likes_count' : 'dislikes_count';
    const { data: updated, error } = await (supabase as any)
      .rpc(type === 'like' ? 'increment_likes' : 'increment_dislikes', { row_id: reviewId });
    
    if (error) {
       // Fallback to simpler update if RPC not defined
       const review = reviews.find(r => r.id === reviewId);
       if (!review) return;
       const newVal = ((review as any)[targetColumn] || 0) + 1;
       const { data: fallbackUpdated } = await (supabase as any)
         .from('reviews')
         .update({ [targetColumn]: newVal })
         .eq('id', reviewId)
         .select()
         .single();
       
       if (fallbackUpdated) {
          setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, [targetColumn]: newVal } : r));
       }
    } else {
       setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, [targetColumn]: (r as any)[targetColumn] + 1 } : r));
    }
  };

  const handleReply = async (reviewId: string, text: string) => {
     if (!userLoggedIn || !text.trim()) return;
     const { data: { user } } = await supabase.auth.getUser();
     if (!user) return;

     const { data: reply, error } = await (supabase as any)
       .from('review_replies')
       .insert({ review_id: reviewId, user_id: user.id, reply_text: text.trim() })
       .select()
       .single();

     if (reply) {
        setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, replies: [...(r.replies || []), reply] } : r));
     }
  };

  const actionButtonClass =
    'inline-flex items-center justify-center gap-2 rounded-xl border border-border/40 bg-muted/30 px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70';

  return (
    <section className="space-y-8">
      <div className="rounded-3xl border border-primary/10 bg-card/80 p-6 sm:p-10 shadow-sm backdrop-blur-md transition-colors relative overflow-hidden">
        {/* Subtle decorative glow */}
        <div className="absolute top-0 right-0 -mt-10 -mr-10 h-32 w-32 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
        
        <h2 className="text-2xl font-black text-foreground flex items-center gap-3">
          <Star className="h-6 w-6 fill-amber-300 text-amber-300" />
          Community Reviews
        </h2>
        <p className="mt-2 text-sm text-muted-foreground font-medium">Hear what professionals in the legal AI industry are saying about {toolName}.</p>

        {userLoggedIn ? (
          <form onSubmit={handleSubmitReview} className="mt-5 space-y-4">
            <StarsInput value={rating} onChange={setRating} />
            <label className="block text-sm font-semibold text-foreground" htmlFor="review-text">
              Share your experience
            </label>
            <textarea
              id="review-text"
              value={reviewText}
              onChange={(event) => setReviewText(event.target.value)}
              className="min-h-[7rem] w-full rounded-xl border border-border/40 bg-muted/40 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70"
              placeholder="How did this tool help your workflow?"
              required
            />
            <button
              type="submit"
              className="rounded-xl bg-gradient-to-r from-primary to-info px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-primary/30 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </form>
        ) : (
          <div className="mt-5 rounded-xl border border-border/40 bg-muted/40 p-4 text-sm text-foreground">
            <p className="mb-3">Login to leave a review.</p>
            <Link
              href="/login"
              className="inline-flex rounded-lg bg-primary px-4 py-2 font-semibold text-white transition hover:bg-primary/90"
            >
              Login
            </Link>
          </div>
        )}

        {statusMessage && <p className="mt-3 text-sm text-muted-foreground">{statusMessage}</p>}

        <div className="mt-6 space-y-3">
          {paginatedReviews.length === 0 && (
            <p className="rounded-xl border border-border/30 bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
              No reviews yet. Be the first to share your experience.
            </p>
          )}
          {paginatedReviews.map((review) => (
            <article
              key={review.id}
              className="rounded-3xl border border-border/20 bg-card/30 p-6 space-y-4"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                   <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20 text-primary font-black text-xs">
                      {review.user_id.slice(0, 2).toUpperCase()}
                   </div>
                   <div>
                      <p className="text-sm font-bold text-foreground leading-none">
                         User_{review.user_id.slice(0, 5)}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1 font-medium italic">
                         {new Date(review.created_at).toLocaleDateString()}
                      </p>
                   </div>
                </div>
                <div className="flex h-fit items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-bold text-amber-500 border border-amber-500/20">
                   <Star className="h-3 w-3 fill-amber-500" />
                   {review.rating}
                </div>
              </div>
              
              <p className="text-sm leading-relaxed text-muted-foreground font-medium pl-13">
                 {review.review_text}
              </p>

              {/* Interaction Row */}
              <div className="flex items-center gap-6 pt-2 pl-13 text-xs font-bold text-muted-foreground">
                 <button 
                  onClick={() => handleVote(review.id, 'like')}
                  className="flex items-center gap-1.5 hover:text-primary transition-all"
                 >
                    <ThumbsUp className="h-3.5 w-3.5" />
                    <span>{review.likes_count || 0}</span>
                 </button>
                 <button 
                  onClick={() => handleVote(review.id, 'dislike')}
                  className="flex items-center gap-1.5 hover:text-rose-500 transition-all"
                 >
                    <ThumbsDown className="h-3.5 w-3.5" />
                    <span>{review.dislikes_count || 0}</span>
                 </button>
                 <button 
                  onClick={() => {
                    setActiveReplyId(activeReplyId === review.id ? null : review.id);
                    setSingleReplyText('');
                  }}
                  className="flex items-center gap-2 hover:text-foreground transition-all text-primary"
                 >
                    <ReplyIcon className="h-3.5 w-3.5" />
                    Reply
                 </button>
              </div>

              {/* Reply Form */}
              {activeReplyId === review.id && (
                <div className="pt-4 pl-13 space-y-3">
                   <textarea
                     value={singleReplyText}
                     onChange={(e) => setSingleReplyText(e.target.value)}
                     className="w-full min-h-[5rem] rounded-xl border border-border/30 bg-muted/30 p-3 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-all"
                     placeholder="Write your reply..."
                   />
                   <div className="flex justify-end gap-3">
                      <button 
                        onClick={() => setActiveReplyId(null)}
                        className="text-xs font-bold text-muted-foreground hover:text-foreground"
                      >
                         Cancel
                      </button>
                      <button 
                        onClick={() => {
                          handleReply(review.id, singleReplyText);
                          setActiveReplyId(null);
                        }}
                        className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-1.5 text-[10px] font-black uppercase text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary/90"
                      >
                         Post <Send className="h-3 w-3" />
                      </button>
                   </div>
                </div>
              )}

              {/* Threaded Replies */}
              {review.replies && review.replies.length > 0 && (
                <div className="mt-4 pl-13 space-y-3">
                   {review.replies.map((reply) => (
                     <div key={reply.id} className="rounded-2xl bg-muted/20 border-l-2 border-primary/30 p-4 space-y-2">
                        <div className="flex items-center gap-2">
                           <p className="text-[10px] font-black text-foreground/50 uppercase tracking-widest">Team {reply.user_id.slice(0, 4)}</p>
                           <div className="h-1 w-1 rounded-full bg-white/20" />
                           <p className="text-[9px] font-medium text-muted-foreground">{new Date(reply.created_at).toLocaleDateString()}</p>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed font-medium">
                           {reply.reply_text}
                        </p>
                     </div>
                   ))}
                </div>
              )}
            </article>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              className="rounded-lg border border-border/40 px-3 py-1.5 text-xs font-semibold text-foreground disabled:opacity-50"
              disabled={page <= 1}
              onClick={() => setPage((prev) => prev - 1)}
            >
              Previous
            </button>
            <span className="text-xs text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="rounded-lg border border-border/40 px-3 py-1.5 text-xs font-semibold text-foreground disabled:opacity-50"
              disabled={page >= totalPages}
              onClick={() => setPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
